import { ContentState } from "draft-js";
import { List } from "immutable";
import { Api } from "@core/api/api";
import { organizationHeaders } from "@core/api/organizationHeaders";
import { filterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { contentStateToString } from "@utils/richText/converters";
import { PostDto, postFromDto } from "@domain/post/dtos";
import { Post } from "@domain/post/post";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { Course, CourseDuration, CourseProvider, DetailedCourse } from "./course";
import { CourseDraft, CourseStatus } from "./courseDraft";
import { courseDraftToDto, CourseDto, courseFromDto, DetailedCourseDto, detailedCourseFromDto } from "./dtos";
import { ExerciseDto, exerciseFromDto } from "@domain/lms/course/exercise/dtos";
import { Exercise } from "@domain/lms/course/exercise/exercise";
import { FeedbackDto, feedbackFromDto } from "@domain/lms/course/feedback/dto";
import { Feedback, FeedbackInput } from "@domain/lms/course/feedback/feedback";

const COURSE_URL = "/courses";

export class CourseApi {
	constructor(private api: Api) {}

	async get(id: string): Promise<Course> {
		if (id.charAt(0) === "@") id = id.substring(1);
		const result = await this.api.get<CourseDto>(`${COURSE_URL}/${id}`);
		return courseFromDto(result.data);
	}
	async getBySlug(slug: string): Promise<Course> {
		const result = await this.api.get<CourseDto>(`${COURSE_URL}?slug=${slug}`);
		return courseFromDto(result.data);
	}

	async getDetailed(id: string): Promise<DetailedCourse> {
		const result = await this.api.get<DetailedCourseDto>(`${COURSE_URL}/${id}/content`);
		return detailedCourseFromDto(result.data);
	}

	async search(
		pageOptions: PaginationOptions,
		query: string,
		categories?: Category[],
		levels?: ContentLevel[],
		languages?: ContentLanguage[],
		durations?: CourseDuration[],
		providers?: CourseProvider[]
	): Promise<Page<Course>> {
		const result = await this.api.get<Page<CourseDto>>(
			`${COURSE_URL}?query=${query}&${pageParams(pageOptions)}&${filterParams(
				categories,
				levels,
				languages,
				durations,
				providers
			)}`
		);

		return { ...result.data, content: result.data.content.map((c) => courseFromDto(c)) };
	}

	async getSuggestions(pageOptions: PaginationOptions, categories: Category[]): Promise<Page<Course>> {
		const result = await this.api.get<Page<CourseDto>>(
			`${COURSE_URL}/suggested?${pageParams(pageOptions)}&categories=${categories.join(",")}`
		);

		return { ...result.data, content: result.data.content.map((c) => courseFromDto(c)) };
	}

	async createCourse(draft: CourseDraft, organizationId?: string): Promise<DetailedCourse> {
		const result = await this.api.post<DetailedCourseDto>(
			COURSE_URL,
			courseDraftToDto(draft),
			organizationHeaders(organizationId)
		);
		return detailedCourseFromDto(result.data);
	}

	async saveCourse(draft: CourseDraft, id: string, organizationId?: string): Promise<DetailedCourse> {
		const result = await this.api.put<DetailedCourseDto>(
			`${COURSE_URL}/${id}`,
			courseDraftToDto(draft),
			organizationHeaders(organizationId)
		);
		return detailedCourseFromDto(result.data);
	}

	delete(courseId: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${COURSE_URL}/${courseId}`, organizationHeaders(organizationId));
	}

	async getFavorites(pageOptions: PaginationOptions): Promise<Page<Course>> {
		const result = await this.api.get<Page<CourseDto>>(`${COURSE_URL}?filter=favorite&${pageParams(pageOptions)}`);

		return { ...result.data, content: result.data.content.map((c) => courseFromDto(c)) };
	}

	async getMyCourses(status: CourseStatus | "ALL", pageOptions: PaginationOptions): Promise<Page<Course>> {
		const result = await this.api.get<Page<CourseDto>>(
			`${COURSE_URL}?filter=mine&status=${status === "ALL" ? "" : status}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => courseFromDto(c)) };
	}

	async getProfileCourses(profileId: string, pageOptions: PaginationOptions): Promise<Page<Course>> {
		const result = await this.api.get<Page<CourseDto>>(`${COURSE_URL}?userId=${profileId}&${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((c) => courseFromDto(c)) };
	}

	async getOrganizationCourses(organizationId: string, pageOptions: PaginationOptions): Promise<Page<Course>> {
		const result = await this.api.get<Page<CourseDto>>(
			`${COURSE_URL}?organizationId=${organizationId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => courseFromDto(c)) };
	}

	async getWorkspaceCourses(workspaceId: string, pageOptions: PaginationOptions): Promise<Page<Course>> {
		const result = await this.api.get<Page<CourseDto>>(
			`${COURSE_URL}?workspaceId=${workspaceId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => courseFromDto(c)) };
	}

	async setFavorite(id: string): Promise<void> {
		await this.api.post<void>(`${COURSE_URL}/${id}/favorite`);
	}

	async removeFavorite(id: string): Promise<void> {
		await this.api.delete<void>(`${COURSE_URL}/${id}/favorite`);
	}

	async sendFeedback(courseId: string, feedback: FeedbackInput): Promise<void> {
		await this.api.post(`${COURSE_URL}/${courseId}/feedbacks`, feedback);
	}

	async getMyFeedback(courseId: string): Promise<Feedback | null> {
		const result = await this.api.get<FeedbackDto>(`${COURSE_URL}/${courseId}/feedbacks/mine`);
		return result.data && feedbackFromDto(result.data);
	}

	async listFeedbacks(courseId: string, pageOptions: PaginationOptions): Promise<Page<Feedback>> {
		const result = await this.api.get<Page<FeedbackDto>>(
			`${COURSE_URL}/${courseId}/feedbacks?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((dto) => feedbackFromDto(dto)) };
	}

	async deleteFeedback(courseId: string): Promise<void> {
		await this.api.delete(`${COURSE_URL}/${courseId}/feedbacks`);
	}

	async share(courseId: string, message?: ContentState, groupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(`${COURSE_URL}/${courseId}/share`, {
			text: message ? contentStateToString(message) : undefined,
			groupId,
		});
		return postFromDto(result.data);
	}

	async validate(courseId: string, chapterIndex: number, answers: List<number>): Promise<Exercise[]> {
		const result = await this.api.post<ExerciseDto[]>(`${COURSE_URL}/${courseId}/validate`, {
			chapterIndex,
			indexes: answers,
		});
		return result.data.map(exerciseFromDto);
	}

	view(courseId: string): Promise<void> {
		return this.api.post(`${COURSE_URL}/${courseId}/view`);
	}
}
